import Cookies from 'js-cookie'
import styled from 'styled-components'
import { Form, Formik } from 'formik'
import { useEffect } from 'react'

import AddressFormField, { FieldType } from 'checkout/components/AddressFormField'
import CountryDropdownField from 'checkout/components/CountryDropdownField'
import Spinner from 'shared/components/Spinner'
import StateDropdownField from 'checkout/components/StateDropdownField'
import { Button } from 'shared/components/Button'
import { CheckoutQA } from 'shared/dataAttributes'
import { STORAGE } from 'shared/enums/SitePreferences'
import { colors, fonts } from 'shared/lib'
import { getAddressValidationSchema } from 'shared/addressValidations'
import { t } from 'localization'
import { useAddressFormContext } from 'checkout/containers/AddressFormContextProvider'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'

const TP = 'checkout.panels.ShippingAddress.NewShippingAddressForm'

export const NewShippingAddressForm = () => {
  const { createShippingAddressForOrderRTK, order, panels, updatePanel } = useCheckoutContext()
  const {
    availableCountries,
    isStateRequired,
    isZipRequired,
    countryISOCode,
    countryUpdateHandler,
  } = useAddressFormContext()

  const checkIfShippable = () => {
    const defaultCountry = availableCountries.find(
      (country) => country.isoCode === Cookies.get(STORAGE.COUNTRY) && country.shipsTo,
    )
    return defaultCountry?.isoCode || ''
  }
  const initialShippingAddress = {
    name: '',
    address1: '',
    address2: '',
    city: '',
    postalCode: '',
    countryCode: checkIfShippable(),
    phone: undefined,
    state: '',
  }

  // Run counntry update handler on mount if country is defaulted by cookie
  useEffect(() => {
    if (initialShippingAddress.countryCode !== '') {
      countryUpdateHandler(initialShippingAddress.countryCode)
    }
  }, [])
  const handleCountryChange = countryUpdateHandler

  return (
    <Spinner
      showSpinner={panels.shippingAddress.loading}
      text={t(`${TP}.spinnerText`, 'Saving address...')}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close your browser!')}
    >
      <Formik
        initialValues={initialShippingAddress}
        onSubmit={createShippingAddressForOrderRTK}
        validationSchema={getAddressValidationSchema(isZipRequired, isStateRequired)}
      >
        {({ dirty, isValid }) => (
          <>
            <Form>
              <AddressFormField
                type={FieldType.FullName}
                title={t(`${TP}.fullName`, '* Full Name')}
                name="name"
                qaAttr={CheckoutQA.NewShippingNameBox}
              />
              <AddressFormField
                type={FieldType.StreetAddress}
                title={t(`${TP}.streetAddress`, '* Street Address')}
                name="address1"
                qaAttr={CheckoutQA.NewShippingAddress1Box}
              />
              <AddressFormField
                type={FieldType.UnitNumber}
                title={t(`${TP}.apartment`, 'Apartment, Suite, Unit, Building, Floor, etc.')}
                name="address2"
                qaAttr={CheckoutQA.NewShippingAddress2Box}
              />
              <AddressFormField
                type={FieldType.City}
                title={t(`${TP}.city`, '* City')}
                name="city"
                qaAttr={CheckoutQA.NewShippingCityBox}
              />
              <AddressFormField
                type={FieldType.ZipCode}
                title={t(`${TP}.zipCode`, {
                  defaultValue: '{zipCode}Zip / Postal Code',
                  zipCode: isZipRequired ? '* ' : '',
                })}
                name="postalCode"
                qaAttr={CheckoutQA.NewShippingZipCodeBox}
              />
              <CountryDropdownField
                availableCountries={availableCountries}
                title={t(`${TP}.countryCode`, '* Country or Region')}
                name="countryCode"
                qaAttr={CheckoutQA.NewShippingCountryBox}
                onChange={handleCountryChange}
              />
              {isStateRequired && (
                <StateDropdownField
                  countryISOCode={countryISOCode}
                  title={t(`${TP}.state`, '* State')}
                  name="state"
                  qaAttr={CheckoutQA.NewShippingStateBox}
                />
              )}
              <AddressFormField
                type={FieldType.Telephone}
                title={t(`${TP}.telephone`, 'Telephone')}
                name="phone"
                qaAttr={CheckoutQA.NewShippingPhoneBox}
              />
              <Pointer>{t(`${TP}.requiredFields`, '* Required Fields')}</Pointer>
              <ContinueButton
                $fill
                buttonType="primary1"
                type="submit"
                disabled={!(isValid && dirty)}
                qaAttr={CheckoutQA.NewShippingContinueButton}
              >
                {t(`${TP}.continue`, 'Continue')}
              </ContinueButton>
            </Form>
            {!!order?.addressId && (
              <Button
                $fill
                buttonType="secondary"
                onClick={() => updatePanel('shippingAddress', { view: 'saved' })}
                qaAttr={CheckoutQA.NewShippingCancelButton}
              >
                {t(`${TP}.cancel`, 'Cancel')}
              </Button>
            )}
          </>
        )}
      </Formik>
    </Spinner>
  )
}

const ContinueButton = styled(Button)`
  margin-bottom: 15px;
`

const Pointer = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  margin: 20px 0;
`
